import { textColor, textSelectColor } from "./helpers/other";
import locale from "./locales/en";
import { IStyle, Modifier, RTEToolbarBlock } from "./types";

export const fontSizes = {
	"12px": true,
	"14px": true,
	"16px": true,
	"18px": true,
	"20px": true,
	"24px": true,
	"28px": true,
	"32px": true,
	"36px": true,
};

function createToolbarFontSizes() {
	const res = [];
	for (const fontSize in fontSizes) {
		const value = parseInt(fontSize, 10);
		res.push({
			id: "font-size-" + value,
			value: fontSize,
		});
	}
	return res;
}

export const fonts = {
	Roboto: true,
	Arial: true,
	Georgia: true,
	Tahoma: true,
	"Times New Roman": true,
	Verdana: true,
};

function createToolbarFonts() {
	const res = [];
	for (const font in fonts) {
		res.push({
			id: "font-" + font,
			value: font,
		});
	}
	return res;
}
export function getToolbarData(toolbarBlocks: RTEToolbarBlock[], defaultStyle: IStyle) {
	const blocks = {
		[RTEToolbarBlock.undo]: [
			{
				id: "undo",
				tooltip: locale.undo,
				type: "button",
				view: "link",
				color: "secondary",
				icon: "dxi-undo",
				multiClick: true,
				disabled: true,
			},
			{
				id: "redo",
				tooltip: locale.redo,
				type: "button",
				view: "link",
				color: "secondary",
				icon: "dxi-redo",
				multiClick: true,
				disabled: true,
			},
		],
		[RTEToolbarBlock.style]: [
			{
				id: Modifier.fontFamily,
				value: defaultStyle[Modifier.fontFamily],
				tooltip: locale.selectFontFamily,
				css: "dhx_richtext__dropdown",
				type: "button",
				view: "link",
				color: "secondary",
				size: "small",
				items: createToolbarFonts(),
			},
			{
				id: Modifier.fontSize,
				value: defaultStyle[Modifier.fontSize],
				tooltip: locale.selectFontSize,
				css: "dhx_richtext__dropdown",
				type: "button",
				view: "link",
				color: "secondary",
				size: "small",
				items: createToolbarFontSizes(),
			},
			{
				id: Modifier.style,
				tooltip: locale.selectFormat,
				value: locale[defaultStyle[Modifier.style]],
				css: "dhx_richtext__dropdown",
				type: "button",
				view: "link",
				color: "secondary",
				size: "small",
				items: [
					{
						id: "style-p",
						value: locale.p,
					},
					{
						id: "style-h1",
						css: "dhx_richtext__menu-text--h1",
						value: locale.h1,
					},
					{
						id: "style-h2",
						css: "dhx_richtext__menu-text--h2",
						value: locale.h2,
					},
					{
						id: "style-h3",
						css: "dhx_richtext__menu-text--h3",
						value: locale.h3,
					},
					{
						id: "style-h4",
						css: "dhx_richtext__menu-text--h4",
						value: locale.h4,
					},
					{
						id: "style-h5",
						css: "dhx_richtext__menu-text--h5",
						value: locale.h5,
					},
					{
						id: "style-h6",
						css: "dhx_richtext__menu-text--h6",
						value: locale.h6,
					},
				],
			},
			{
				id: Modifier.blockquote,
				tooltip: locale.blockquote,
				active: defaultStyle[Modifier.blockquote],
				type: "button",
				view: "link",
				color: "secondary",
				size: "small",
				icon: "dxi-format-quote-open",
			},
		],
		[RTEToolbarBlock.color]: [
			{
				id: Modifier.color,
				tooltip: locale.selectTextColor,
				type: "customButton",
				css: "dhx_button--toolbar dhx_button--color_secondary",
				color: "secondary",
				html: textColor(defaultStyle[Modifier.color]),
			},
			{
				id: Modifier.background,
				tooltip: locale.selectTextBackground,
				type: "customButton",
				css: "dhx_button--toolbar dhx_button--color_secondary",
				color: "secondary",
				html: textSelectColor(defaultStyle[Modifier.background]),
			},
		],
		[RTEToolbarBlock.decoration]: [
			{
				id: Modifier.bold,
				tooltip: locale.markBold,
				icon: "dxi-format-bold",
				type: "button",
				view: "link",
				color: "secondary",
				hotkey: "ctrl+b",
				active: defaultStyle[Modifier.bold],
			},
			{
				id: Modifier.italic,
				tooltip: locale.markItalic,
				icon: "dxi-format-italic",
				type: "button",
				view: "link",
				color: "secondary",
				hotkey: "ctrl+i",
				active: defaultStyle[Modifier.italic],
			},
			{
				id: Modifier.strike,
				tooltip: locale.markStrike,
				icon: "dxi-format-strikethrough",
				type: "button",
				color: "secondary",
				view: "link",
				active: defaultStyle[Modifier.strike],
			},
			{
				id: Modifier.underline,
				tooltip: locale.markUnderline,
				icon: "dxi-format-underline",
				type: "button",
				view: "link",
				color: "secondary",
				hotkey: "ctrl+u",
				active: defaultStyle[Modifier.underline],
			},
		],
		[RTEToolbarBlock.align]: [
			{
				id: "align-left",
				tooltip: locale.alignLeft,
				type: "button",
				view: "link",
				color: "secondary",
				icon: "dxi-format-align-left",
				active: defaultStyle[Modifier.align] === "left",
			},
			{
				id: "align-center",
				tooltip: locale.alignCenter,
				type: "button",
				view: "link",
				color: "secondary",
				icon: "dxi-format-align-center",
				active: defaultStyle[Modifier.align] === "center",
			},
			{
				id: "align-right",
				tooltip: locale.alignRight,
				type: "button",
				view: "link",
				color: "secondary",
				icon: "dxi-format-align-right",
				active: defaultStyle[Modifier.align] === "right",
			},
		],
		[RTEToolbarBlock.link]: [
			{
				id: Modifier.link,
				tooltip: locale.addLink,
				type: "button",
				view: "link",
				color: "secondary",
				icon: "dxi-link-variant",
			},
		],
		[RTEToolbarBlock.clear]: [
			{
				id: "clear-style",
				tooltip: locale.clearFormat,
				type: "button",
				view: "link",
				color: "secondary",
				icon: "dxi-eraser",
			},
		],
		[RTEToolbarBlock.stats]: [
			{
				id: "stats",
				tooltip: locale.stats,
				type: "button",
				view: "link",
				color: "secondary",
				icon: "dxi-information-outline",
			},
		],
		[RTEToolbarBlock.fullscreen]: [
			{
				id: "fullscreen",
				$fullscreen: false,
				tooltip: locale.fullscreen,
				type: "button",
				view: "link",
				color: "secondary",
				icon: "dxi-arrow-expand",
			},
		],
	};

	const result = toolbarBlocks.reduce((config, block, i) => {
		config.push(...blocks[block]);
		if (i !== toolbarBlocks.length - 1) {
			config.push({ type: "separator" });
		}
		return config;
	}, []);

	return result;
}

export const emptyStyle = {};

export const defaultBlocks = [
	RTEToolbarBlock.undo,
	RTEToolbarBlock.style,
	RTEToolbarBlock.decoration,
	RTEToolbarBlock.color,
	RTEToolbarBlock.align,
	RTEToolbarBlock.link,
];

export const defaultFontSizeByBlock = {
	p: "14px",
	h1: "36px",
	h2: "32px",
	h3: "28px",
	h4: "24px",
	h5: "20px",
	h6: "16px",
};

export const defaultDiffrentStyle = {
	[Modifier.fontSize]: "",
	[Modifier.fontFamily]: "",
	[Modifier.style]: "",
	[Modifier.bold]: false,
	[Modifier.underline]: false,
	[Modifier.bold]: false,
	[Modifier.italic]: false,
	[Modifier.strike]: false,
	[Modifier.color]: "#4c4c4c",
	[Modifier.background]: "#FFFFFF",
	[Modifier.align]: false,
};
